/**
 * `GET /tracking` — what this workspace's email actually carries.
 *
 *   fetchTracking   GET /tracking   the two switches, the redirect domain, the
 *                                   configuration set, and the disclosure text
 *
 * **Why a screen that is not the tracking switch reads this.** Slice 4 wrote a
 * promise into this product's copy — *an email Wend sends makes no network
 * request when it is opened* — and slice 5 spent real effort keeping it: the
 * MJML Google Fonts `<link>` was stripped, and inline images became CID parts
 * rather than URLs so that opening a message fetches nothing from anyone. That
 * sentence is printed in the template editor, beside the preview, and next to
 * the test send, because that is where a person is standing when they wonder
 * what they are about to put in somebody's inbox.
 *
 * Slice 11 makes the sentence **conditional**. With tracking on, Amazon injects
 * a 1×1 pixel and rewrites every link — server-side, after Wend has handed the
 * message over. So the editor can no longer print a fixed sentence: it has to
 * read the setting and print whichever of two opposite sentences is true for
 * this workspace. This function is how it reads it.
 *
 * **The rejected alternative was to import the constants and guess.**
 * `TRACKING_DISCLOSURE` and `TRACKING_OFF` are both in `@wend/shared`, so the
 * editor could have rendered the off-copy from the constant and skipped the
 * request entirely. That is exactly the bug this slice exists to remove: the
 * default is off, so guessing is right for almost every workspace and wrong for
 * precisely the ones who switched tracking on and now need the truth most. A
 * promise that is right by default and wrong on purpose is worse than no
 * promise.
 *
 * **`disclosure` is read off the wire rather than imported**, for the reason the
 * contract gives: the API writes an audit entry with the text a person was shown
 * when tracking changed, and the screen showing a paraphrase of it — because a
 * frontend build lagged a constant — would make "what were they told" a question
 * with two answers. One sentence, one source, even though it costs a few hundred
 * bytes on a response this screen reads once.
 *
 * The response is parsed with `trackingResponseSchema` like every other read in
 * this client, so an API that stopped carrying `settings.click` fails here
 * rather than rendering the strong promise from an `undefined`.
 *
 * **A seam to close, stated rather than left to be discovered.** Slice 11 was
 * built by several agents at once, and `routes/integrations/tracking-api.ts`
 * declares its own `fetchTracking` against the same path and the same schema —
 * deliberately, with a note in its own header saying it belongs in `lib/api/`
 * and was kept beside its screen only to avoid two people editing one shared
 * file in one afternoon. This is that file. The two reads should collapse into
 * this one, with the write side (`PUT /tracking`, the CNAME flow, provisioning)
 * moving alongside it; nothing about either depends on the location. Neither
 * duplicates a *shape* — both parse `trackingResponseSchema` from
 * `@wend/shared` — so the two cannot disagree about what a tracking setting is
 * while the seam is open, which is what makes it safe to leave for one merge.
 *
 * **The route path is an assumption both files make.** `@wend/shared` carries
 * the shapes and not the paths, and `GET /tracking` did not exist in `apps/api`
 * when this was written. Until it does, this read fails and the screens using it
 * say "could not read this workspace's tracking settings" — which is the correct
 * behaviour for an unbuilt endpoint and is the reason that state exists.
 */
import { trackingResponseSchema, type TrackingResponse } from '@wend/shared';

import { apiRequest } from './client';

/**
 * This workspace's tracking settings.
 *
 * No query and no argument: the settings belong to the tenant, and the tenant is
 * the bearer token. A `templateId` parameter would suggest tracking can differ
 * per template, which it cannot — a configuration set is named on a send, and
 * every send from a workspace chooses it from the same settings.
 *
 * Callers are expected to survive this **failing**. A workspace whose settings
 * could not be read is not a workspace with tracking off; see
 * `lib/recipient-experience.ts`, which is where that distinction is kept.
 */
export async function fetchTracking(signal?: AbortSignal): Promise<TrackingResponse> {
  return apiRequest({
    path: '/tracking',
    schema: trackingResponseSchema,
    signal,
  });
}
